import React, { useEffect, useState } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import moment from "moment";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";

const UserList = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
      try {
        const response = await api.get("/admin/users");
        setUsers(Array.isArray(response.data) ? response.data : []);
      } catch {
        toast.error(t("userList_fetchError"));
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    setLoading(true);
    
    fetchUsers();
  }, []);

  const handleRemove = async (userId) => {
    toast((toastInstance) => (
        <div className="flex items-center gap-3">
            <span className="text-sm">{t("userList_removeConfirmTitle")}</span>
            <button
                onClick={async () => {
                    try {
                        await api.delete(`/admin/users/remove/${userId}`);
                        toast.success(t("userList_removeSuccess"));
                        fetchUsers();
                    } catch (err) {
                        console.error(err);
                        toast.error(t("userList_removeError"));
                    }
                    toast.dismiss(toastInstance.id);
                }}
                className="text-xs text-red-400 hover:text-red-300"
            >
                {t("userList_removeConfirmYes")}
            </button>
            <button
                onClick={() => toast.dismiss(toastInstance.id)}
                className="text-xs text-zinc-400 hover:text-zinc-300"
            >
                {t("userList_removeConfirmCancel")}
            </button>
        </div>
    ));
  };

  return (
    <div className="min-h-[calc(100vh-64px)] px-4 py-12">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <span className="text-xs font-mono uppercase tracking-widest text-violet-400">{t("userList_label")}</span>
          <h1 className="text-2xl font-bold text-white mt-1">{t("userList_title")}</h1>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <Loader2 size={28} className="animate-spin text-violet-400" />
            <span className="text-sm text-zinc-500">{t("userList_loading")}</span>
          </div>
        ) : (
          <div className="rounded-xl border border-white/8 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/8 hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium">{t("userList_tableUsername")}</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium">{t("userList_tableEmail")}</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium">{t("userList_tableRole")}</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium">{t("userList_tableCreated")}</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium">{t("userList_tableStatus")}</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-white/5 hover:bg-white/3 transition-colors">
                    <TableCell className="text-white text-sm font-medium">{user.username}</TableCell>
                    <TableCell className="text-zinc-400 text-sm">{user.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${
                        user.role === "ADMIN"
                          ? "text-violet-400 bg-violet-400/10 border-violet-400/20"
                          : "text-zinc-400 bg-zinc-400/10 border-zinc-400/20"
                      }`}>
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-zinc-500 text-xs">
                      {moment(user.createdAt).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 text-xs ${
                        user.enabled ? "text-green-400" : "text-zinc-600"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${user.enabled ? "bg-green-400" : "bg-zinc-600"}`} />
                        {user.enabled ? t("userList_statusActive") : t("userList_statusInactive")}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Link to={`/admin/users/${user.id}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs border-white/10 text-zinc-400 hover:text-white hover:border-white/20 bg-transparent"
                        >
                          {t("userList_viewButton")}
                        </Button>
                      </Link>
                    </TableCell>
                    <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs border-white/10 text-zinc-400 hover:text-white hover:border-white/20 bg-transparent"
                          onClick={() => handleRemove(user.id)}
                        >
                          {t("userList_removeButton")}
                        </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;
